import React, { useEffect, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Move } from "@/types";
import { useUpdateMoveMutation } from "@/store/moves/apis";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import * as chrono from "chrono-node";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";

const FUReminder = ({
  move,
  triggerChildren,
  refetchMove,
  manualHandlers,
}: {
  move: Move;
  triggerChildren: React.ReactNode;
  refetchMove: () => void;
  manualHandlers?: {
    onOpenChange: (open: boolean) => void;
    open: boolean;
  };
}) => {
  const { toast } = useToast();
  const [reminder, setReminder] = useState<{
    date: Date | null;
    notes: string;
  } | null>(null);
  const [updateMove] = useUpdateMoveMutation();
  const [isCustomReminderOpen, setIsCustomReminderOpen] = useState(false);
  // const [customDate, setCustomDate] = useState<Date>(new Date());
  // const [customHour, setCustomHour] = useState("");
  // const [customMinute, setCustomMinute] = useState("");
  // const [customAmPm, setCustomAmPm] = useState<"AM" | "PM">("AM");
  const [customNotes, setCustomNotes] = useState("");
  const [relativeDateTime, setRelativeDateTime] = useState("");
  const [parsedDate, setParsedDate] = useState<Date | null>(null);

  useEffect(() => {
    if (!reminder) return;

    const saveReminder = async () => {
      // if (!reminder) {
      //   toast({
      //     title: "Error",
      //     description: "Reminder not set",
      //     variant: "destructive",
      //   });
      //   return;
      // }
      try {
        await updateMove({
          id: move.id,
          move: {
            reminders_attributes: [
              {
                when: reminder.date,
                notes: reminder.notes,
              },
            ],
          },
        }).unwrap();
        setReminder(null);
        refetchMove();
      } catch (err) {
        console.log("err", err);
      }
    };

    saveReminder();
  }, [reminder, move, refetchMove, updateMove, toast]);

  const handleSetReminder = async (
    date: Date,
    time: string,
    notes: string = ""
  ) => {
    const combinedDate = new Date(`${date.toDateString()} ${time}`);
    setReminder({ date: combinedDate, notes });
    setIsCustomReminderOpen(false);
    toast({
      title: "Reminder Set!",
      description: `Follow-up reminder added for ${combinedDate.toLocaleString(
        "en-US",
        {
          weekday: "short",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        }
      )}`,
    });
    // await saveReminder();
  };

  const handleCustomReminderSave = async () => {
    if (!parsedDate) {
      toast({
        title: "Error",
        description: "Invalid date. Please enter a valid date and time.",
        variant: "destructive",
      });

      return;
    }

    const reminder = {
      date: parsedDate,
      notes: customNotes,
    };

    setReminder(reminder);

    setIsCustomReminderOpen(false);
    setRelativeDateTime("");
    setParsedDate(null);
    setCustomNotes("");

    toast({
      title: "Reminder Set!",
      description: `Follow-up reminder added for ${reminder.date.toLocaleString(
        "en-US",
        {
          weekday: "short",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        }
      )}`,
    });
    // await saveReminder();
  };

  const generateTimeOptions = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const options = [];

    for (let i = currentHour + 1; i <= 17; i++) {
      options.push(
        <DropdownMenuItem
          key={i}
          onSelect={() => handleSetReminder(now, `${i}:00`)}
        >
          {i <= 12 ? i : i - 12}
          {i < 12 ? " AM" : " PM"}
        </DropdownMenuItem>
      );
    }

    return options;
  };

  const handleRelativeDateTimeChange = (value: string) => {
    setRelativeDateTime(value);
    const parsed = chrono.parseDate(value);
    setParsedDate(parsed);
  };

  const tomorrowHours = Array.from({ length: 10 }, (_, i) => i + 8);

  // const handleTomorrowTimeSelect = (hour: number) => {
  //   const tomorrow = new Date();
  //   tomorrow.setDate(tomorrow.getDate() + 1);
  //   tomorrow.setHours(hour, 0, 0, 0);

  //   const reminder = {
  //     date: tomorrow,
  //     notes: "", // You can set a default note if needed
  //   };

  //   setReminder(reminder);
  //   toast({
  //     title: "Success",
  //     description: `Reminder set for tomorrow at ${
  //       hour <= 12 ? `${hour}:00 AM` : `${hour - 12}:00 PM`
  //     }`,
  //   });
  // };

  return (
    <div>
      <DropdownMenu {...manualHandlers} modal={false}>
        <DropdownMenuTrigger asChild>{triggerChildren}</DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel>Set a follow-up reminder</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Today</DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuItem
                  onSelect={() =>
                    handleSetReminder(
                      new Date(),
                      `${new Date().getHours()}:${new Date().getMinutes() + 15}`
                    )
                  }
                >
                  in 15m
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() =>
                    handleSetReminder(
                      new Date(),
                      `${new Date().getHours()}:${new Date().getMinutes() + 30}`
                    )
                  }
                >
                  in 30m
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() =>
                    handleSetReminder(
                      new Date(),
                      `${new Date().getHours() + 1}:${new Date().getMinutes()}`
                    )
                  }
                >
                  in 1h
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() =>
                    handleSetReminder(
                      new Date(),
                      `${new Date().getHours() + 2}:${new Date().getMinutes()}`
                    )
                  }
                >
                  in 2h
                </DropdownMenuItem>
                {generateTimeOptions()}
                <DropdownMenuItem
                  onSelect={() => setIsCustomReminderOpen(true)}
                >
                  Custom
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Tomorrow</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {tomorrowHours.map((hour) => (
                <DropdownMenuItem
                  key={hour}
                  onSelect={() =>
                    handleSetReminder(
                      new Date(new Date().setDate(new Date().getDate() + 1)),
                      `${hour}:00`
                    )
                  }
                >
                  {hour == 12
                    ? `${hour} PM`
                    : hour < 12
                    ? `${hour} AM`
                    : `${hour - 12} PM`}
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem onSelect={() => setIsCustomReminderOpen(true)}>
                Custom
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem onSelect={() => setIsCustomReminderOpen(true)}>
            Another day/time
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Current Reminders</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {move.reminders.map(
            (reminder: { id: number; when: Date; notes: string }) => (
              <DropdownMenuItem
                className="flex flex-col items-start"
                key={reminder.id}
              >
                <p>
                  in {formatDistanceToNow(reminder.when)}{" "}
                  <span className="text-sm">
                    -{" "}
                    {new Date(reminder.when).toLocaleString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                  </span>
                </p>
                <p className="text-xs text-stone-700">{reminder.notes}</p>
              </DropdownMenuItem>
            )
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={isCustomReminderOpen}
        onOpenChange={() => setIsCustomReminderOpen(false)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Custom Reminder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <Input
                placeholder="Enter date/time in plain english (e.g., 'tomorrow at 3pm', 'next thur at 11am')"
                value={relativeDateTime}
                onChange={(e) => handleRelativeDateTimeChange(e.target.value)}
              />
              {parsedDate ? (
                <p className="text-sm text-gray-900 flex justify-between items-center">
                  <span>
                    {parsedDate
                      .toLocaleString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                      .replace(/,/g, "")
                      .replace(
                        /(\w+)\s(\d{2})\/(\d{2})\/(\d{4})\s/,
                        "$1 $2/$3/$4 at "
                      )}
                  </span>
                  <span>
                    {formatDistanceToNow(parsedDate, {
                      addSuffix: true,
                    })}{" "}
                  </span>
                </p>
              ) : (
                <span></span>
              )}
            </div>
            <Input
              placeholder="Notes"
              value={customNotes}
              onChange={(e) => setCustomNotes(e.target.value)}
            />
            <Button onClick={handleCustomReminderSave}>Save Reminder</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FUReminder;
